/**
 * This is the needs analysis page / set of pages
 */
import type { ComponentProps } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, useNavigate } from '@reach/router';
import StepWizard from 'react-step-wizard';
import { usePolicyStreamGoogleAnalytics } from '../../hooks/usePolicyStreamGoogleAnalytics';
import {
  GAEventNames,
  EventActions,
} from '../../hooks/usePolicyStreamGoogleAnalytics/types';
import { useEnsureFormPresent } from '../../hooks/useEnsureFormPresent';
import { PolicyStreamPageContainer } from './components/PageContainer';
import { PolicystreamForm } from './components/PolicystreamForm';
import { POLICYSTREAM_FORMS } from './constants';
import { policyStreamPaths } from './policyStreamConstants';
import { QuoteWizardForm } from '../QuoteWizard/classes';
import { useSetPolicyStreamSaveButtonVisibility } from '../../hooks';
import { PolicyStreamLayout } from './components';
import { MAX_AGE, MIN_AGE } from './constants';
import { useSelector } from 'react-redux';
import { PolicyStreamInvalidAge } from './components/PolicyStreamInvalidAge';
import { getPolicyStreamClientAgeYears } from '../../store/utility/policyStreamUtility';
import {
  MissingInformation,
  useMissingBasicInfoGuard,
} from './components/MissingInformation';
import { useSetStepStatus } from '../../hooks';
import {
  generateGetPolicyStreamQuizFormAnsweredForm,
  policyStreamQuizFormInitialValuesFromAnsweredForm,
} from '../../store/policystreamStore';
import type { InitialValues } from '../../Typescript/forms';

export const PolicyStreamQuizPage = (_props: RouteComponentProps) => {
  const myAnalytics = usePolicyStreamGoogleAnalytics();

  useSetPolicyStreamSaveButtonVisibility(false);
  const [splitForms, setSplitForms] = useState<Array<QuoteWizardForm>>([]);
  const navigate = useNavigate();
  const recommendationForm = useEnsureFormPresent({
    internalName: POLICYSTREAM_FORMS.needsAnalysis,
  });
  const clientAge = useSelector(getPolicyStreamClientAgeYears);
  const setStepStatus = useSetStepStatus('Needs Analysis');

  // Send Google Analytics event on component load
  useEffect(() => {
    myAnalytics?.triggerEvent(
      GAEventNames.Personalize,
      EventActions.PageLoad,
      'PolicyStream Personalize Page',
    );
  }, [myAnalytics]);

  // set the step status once we arrive
  useEffect(() => {
    setStepStatus('IN_PROGRESS');
  }, []);

  const missingInfo = useMissingBasicInfoGuard();

  useEffect(() => {
    let split: Array<QuoteWizardForm> = [];
    if (recommendationForm) {
      split = [
        ...split,
        ...recommendationForm.questionInstances.map(
          (question) =>
            new QuoteWizardForm({
              ...recommendationForm,
              questionInstances: [question],
            }),
        ),
      ];
    }
    setSplitForms(split);
  }, [recommendationForm /* basicInfoForm */]);

  return (
    <PolicyStreamLayout>
      {missingInfo ? (
        <MissingInformation
          missing={missingInfo.missing}
          redirectURL={policyStreamPaths.baseInformation.url}
          stepName='Needs Analysis'
        />
      ) : (
        <PolicyStreamPageContainer>
          <h2>Personalize</h2>
          {clientAge !== null &&
          (clientAge < MIN_AGE || clientAge > MAX_AGE) ? (
            <PolicyStreamInvalidAge />
          ) : (
            <StepWizard isLazyMount>
              {splitForms.map((form, formIndex) => (
                <PolicyStreamQuizStepForm
                  key={formIndex}
                  onFirstPrev={() =>
                    navigate(policyStreamPaths.baseInformation.url)
                  }
                  onLastNext={() => {
                    setStepStatus('COMPLETED');
                    navigate(policyStreamPaths.emailSave.url);
                  }}
                  form={form}
                />
              ))}
            </StepWizard>
          )}
        </PolicyStreamPageContainer>
      )}
    </PolicyStreamLayout>
  );
};

type PolicyStreamQuizStepFormProps = Omit<
  ComponentProps<typeof PolicystreamForm>,
  'initialValues'
> & {
  form: QuoteWizardForm;
};

/**
 * One wizard step: subscribes to saved answers for this form id and passes them
 * into {@link PolicystreamForm} as `initialValues` (null when nothing is in the store yet).
 */
const PolicyStreamQuizStepForm = ({
  form,
  ...rest
}: PolicyStreamQuizStepFormProps) => {
  const selectAnsweredForm = useMemo(
    () => generateGetPolicyStreamQuizFormAnsweredForm(form),
    [form.id],
  );
  const answeredForm = useSelector(selectAnsweredForm);
  const initialValuesFromStore = useMemo(
    () => policyStreamQuizFormInitialValuesFromAnsweredForm(form, answeredForm),
    [answeredForm, form],
  );
  const [initialValues, setInitialValues] = useState<InitialValues | null>(
    null,
  );

  useEffect(() => {
    setInitialValues(initialValuesFromStore);
  }, [initialValuesFromStore]);

  return (
    <PolicystreamForm form={form} initialValues={initialValues} {...rest} />
  );
};
